import { useMemo, useState } from "react";
import { useGetCompaniesForAdmin } from "@/services/Company";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { AdminCompanyListItem, CompanyPendingAction } from "@/types";

import Table from "@/components/atoms/Table";
import SortableHeader from "../../molecules/SortableHeader";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import PageWrapper from "../../molecules/PageWrapper";
import Text from "@/components/atoms/Text";
import Group from "@/components/atoms/Group";
import Loader from "@/components/atoms/Loader";
import TextInput from "@/components/atoms/TextInput";
import CompanyApplicationAdminDrawer from "./Drawers/PartialCompanyDrawer";
import CompanyDrawer from "./Drawers/CompanyDrawer";
import CompanyPendingActionTypeEnum from "@/constants/enums/CompanyPendingActionTypeEnum";
import Checkbox from "@/components/atoms/Checkbox";
import NumberFormatter from "@/components/atoms/NumberFormatter";
import DnbRiskLevel from "./DnbRiskLevel";

import styles from "./styles.module.css";

export default function CompanyApplicationAdmin() {
  const { t } = useTranslation();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [companyInDrawer, setCompanyInDrawer] = useState<number>(0);
  const [companyListItemInDrawer, setCompanyListItemInDrawer] = useState<AdminCompanyListItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState<"company" | "partialCompany" | false>(false);
  const [globalFilter, setGlobalFilter] = useState<any>([]);

  const { data: companiesData, isFetching: isFetchingCompanies } = useGetCompaniesForAdmin(10000, 0);
  console.log("Companies data fetched:", companiesData);
  const columns: ColumnDef<AdminCompanyListItem>[] = useMemo(
    () => [
      {
        header: () => <></>,
        size: 30,
        minSize: 30,
        maxSize: 30,
        accessorKey: "symbol",
        cell: ({ row }) =>
          row.original.hasPendingAction || row.original.partialCompanyId ? (
            <SVGAsset name="yellow-exclamation" />
          ) : null,
      },
      {
        header: ({ column }) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <SortableHeader column={column} label={t("commonAdmin:tradeLock")} />
          </div>
        ),
        accessorKey: "locked",
        cell: ({ row }) => {
          return (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Checkbox checked={row.original.locked} />
            </div>
          );
        },
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:companyName")} />,
        accessorKey: "name",
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:vatNumber")} />,
        accessorKey: "vatNumber",
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("DUNS")} />,
        accessorKey: "dunsNumber",
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:country")} />,
        accessorKey: "countryIso3",
      },
      {
        id: "status",
        header: ({ column }) => <SortableHeader column={column} label={t("commonAdmin:status")} />,
        accessorFn: (row) => t(`commonAdmin:companyStatus.${row.status}`),
        cell: ({ row }) => <>{t(`commonAdmin:companyStatus.${row.original.status}`)}</>,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("commonAdmin:dnbRiskLevel")} />,
        accessorKey: "companyRisk",
        cell: ({ row }) => <DnbRiskLevel level={row.original.companyRisk} />,
      },
      {
        header: ({ column }) => <SortableHeader column={column} label={t("common:creditLimit")} />,
        accessorKey: "creditLimit",
        cell: ({ row }) => <NumberFormatter value={row.original.creditLimit} />,
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: companiesData ? companiesData.items : [],
    columns,
    state: { globalFilter, sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
  });

  return (
    <>
      <PageWrapper>
        <div className={styles.contentRoot}>
          <Group className={styles.headerRoot}>
            <Text size="xl">{t("commonAdmin:companyAdmin")}</Text>
            <TextInput
              placeholder={t("actions:search")}
              rightSection={<SVGAsset name="search" />}
              onChange={(e) => table.setGlobalFilter(e.target.value)}
            />
            {isFetchingCompanies ? <Loader /> : null}
          </Group>

          <Table className={styles.tableRoot}>
            <Table.Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.Th
                      key={header.id}
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Table.Th>
                  ))}
                </Table.Tr>
              ))}
            </Table.Thead>
            <Table.Tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <Table.Tr
                    key={row.id}
                    style={{
                      backgroundColor:
                        row.original.hasPendingAction ||
                        row.original.partialCompanyId ||
                        row.original.pendingActions?.some(
                          (action: CompanyPendingAction) =>
                            (action.type === CompanyPendingActionTypeEnum.CREDIT_REQUEST &&
                              action.status !== "RESOLVED") ||
                            (CompanyPendingActionTypeEnum.COMPANY_INVITED && action.status !== "RESOLVED")
                        )
                          ? "var(--color-warning-secondary)"
                          : "transparent",
                    }}
                    onClick={() => {
                      setCompanyInDrawer(
                        row.original.companyId ? row.original.companyId : row.original.partialCompanyId!
                      );
                      setCompanyListItemInDrawer(row.original);
                      setIsDrawerOpen(row.original.companyId ? "company" : "partialCompany");
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Table.Td>
                    ))}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </div>
      </PageWrapper>
      <CompanyApplicationAdminDrawer
        partialCompanyId={companyInDrawer}
        onClose={() => {
          setIsDrawerOpen(false);
          setCompanyInDrawer(0);
        }}
        isOpen={isDrawerOpen === "partialCompany"}
      />
      <CompanyDrawer
        adminCompanyListItem={companyListItemInDrawer}
        companyId={companyInDrawer}
        onClose={() => {
          setIsDrawerOpen(false);
          setCompanyInDrawer(0);
        }}
        isOpen={isDrawerOpen === "company"}
      />
    </>
  );
}
